import React, { useEffect, useState, useCallback, useRef } from "react";
import Sidebar from "components/Sidebar/Sidebar.js";
import axios from "axios";
import { Spinner } from "components/Spinner";
import config from "services/config";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  CloudUpload,
  Description,
  Close,
  Download,
  FolderShared,
  DeleteOutline,
} from "@mui/icons-material";
import {
  Button,
  IconButton,
  TextField,
  Chip,
  Box,
  Typography,
  InputAdornment,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "context/auth/auth";
import { useLayout } from "context/layout/layout";
import { useLegacy } from "context/legacy/legacy";
import {
  AccountBalance,
  AddCircle,
  Upload,
  Search,
  Person,
  FilterList,
} from "@mui/icons-material";
import { colors } from "utils/theme";
import * as XLSX from "xlsx";

const formatDateLocal = (isoString) => {
  if (!isoString) return "—";
  const date = new Date(isoString);
  return date.toLocaleDateString("es-CL", { timeZone: "UTC" });
};

export const MembersList = React.memo(({ color = null }) => {
  const { legacy, setLegacy } = useLegacy();
  const auth = useAuth();
  const layout = useLayout();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [temp, setTemp] = useState(legacy.members);
  const [query, setQuery] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [showExpiredOnly, setShowExpiredOnly] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, member: null });

  const handleFileSelect = (selectedFile) => {
    setFile(selectedFile);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.name.endsWith(".xlsx")) {
      handleFileSelect(droppedFile);
    }
  };

  const handleDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setDragOver(false); };
  const removeFile = () => { setFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; };
  const openFileDialog = () => { fileInputRef.current?.click(); };

  const handleBulkMember = async () => {
    if (!file) return;
    setLoading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await fetch(`${config.url}/members/bulk`, { method: "POST", body: formData });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.error || `HTTP ${res.status}`); }
      const data = await res.json();
      layout.successAlert({ message: `${data.inserted} clientes importados` });
    } catch (err) {
      console.error(err);
      layout.errorAlert({ message: "Error al importar" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(false);
    if (legacy.members?.length < 1) {
      setLoading(true);
      axios.get(`${config.url}/members/showall`).then((res) => {
        setLegacy({ ...legacy, members: res.data });
        setTemp(res.data);
        setLoading(false);
      }).catch((err) => { console.log(err); setLoading(false); });
    }
    return () => { setLegacy({ ...legacy, members: temp }); };
  }, []);

  useEffect(() => {
    let filtered = [...(temp || [])];
    if (query.length > 0) {
      filtered = filtered.filter((p) =>
        p.id_number?.includes(query) || p.name?.toLowerCase().includes(query.toLowerCase()) || p.company_information?.razon_social?.toLowerCase().includes(query.toLowerCase())
      );
    }
    if (showExpiredOnly) {
      filtered = filtered.filter((m) => new Date(m.subscription?.endDate) < new Date() && m.enabled);
    }
    setLegacy({ ...legacy, members: filtered });
  }, [query, temp, showExpiredOnly]);

  const seeDetails = useCallback(({ member_id }) => { navigate(`/members/view/${member_id}`); }, [navigate]);

  async function handleDeleteMember(memberId) {
    try {
      setLoading(true);
      await axios.delete(`${config.url}/members/delete/${memberId}`);
      const updated = legacy.members.filter((m) => m._id !== memberId);
      setLegacy({ ...legacy, members: updated });
      setTemp(updated);
      layout.successAlert({ message: "Socio eliminado exitosamente" });
    } catch (err) {
      layout.errorAlert({ message: "Error al eliminar el socio" });
    } finally {
      setLoading(false);
    }
  }

  const getStatusColor = (endDate) => new Date(endDate) > new Date() ? "success" : "warning";
  const getStatusText = (endDate) => new Date(endDate) > new Date() ? "Activo" : "Vencido";

  const totalMembers = legacy.members?.length || 0;
  const activeMembers = legacy.members?.filter((m) => new Date(m.subscription?.endDate) > new Date()).length || 0;
  const expiredMembers = totalMembers - activeMembers;

  const handleExportExcel = () => {
    const dataToExport = legacy.members.map((member) => ({
      Nombre: member.name || "",
      Email: member.email || "",
      RUT: member.id_number || "",
      Plan: member.subscription?.type || "",
      "Término": member.subscription?.endDate ? formatDateLocal(member.subscription.endDate) : "",
      Precio: member.subscription?.membership?.price || "",
      Estado: new Date(member.subscription?.endDate) < new Date() ? "Vencido" : "Al día",
    }));
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(dataToExport);
    ws["!cols"] = [{ wch: 30 }, { wch: 30 }, { wch: 14 }, { wch: 25 }, { wch: 14 }, { wch: 10 }, { wch: 10 }];
    XLSX.utils.book_append_sheet(wb, ws, "Clientes");
    XLSX.writeFile(wb, `clientes-${new Date().toISOString().split("T")[0]}.xlsx`);
  };

  const StatCard = ({ value, label, icon: Icon, accent }) => (
    <div
      style={{
        background: "#fff",
        borderRadius: "0.75rem",
        padding: "1.25rem 1.5rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        border: "1px solid #f1f5f9",
        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
      }}
    >
      <div>
        <p style={{ fontSize: "1.75rem", fontWeight: 700, color: "#0f172a", margin: 0, lineHeight: 1.2 }}>{value}</p>
        <p style={{ fontSize: "0.8rem", color: "#64748b", margin: 0, marginTop: "0.25rem", fontWeight: 500 }}>{label}</p>
      </div>
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: "0.625rem",
          background: accent + "12",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icon sx={{ fontSize: 22, color: accent }} />
      </div>
    </div>
  );

  return (
    <>
      {loading && <Spinner />}
      <Sidebar />

      <div style={{ marginLeft: 210, minHeight: "100vh", background: "#f8fafc" }}>
        {/* Header */}
        <div
          style={{
            padding: "1.75rem 2rem 1.5rem",
            borderBottom: "1px solid #e2e8f0",
            background: "#fff",
          }}
        >
          <div style={{ maxWidth: 1400, margin: "0 auto" }}>
            <p style={{ fontSize: "0.75rem", fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", margin: 0, marginBottom: "0.25rem" }}>
              Panel de gestion
            </p>
            <h1 style={{ fontSize: "1.5rem", fontWeight: 700, color: "#0f172a", margin: 0 }}>
              Clientes
            </h1>
          </div>
        </div>

        <div style={{ maxWidth: 1400, margin: "0 auto", padding: "1.5rem 2rem" }}>
          {/* Stats */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "1rem", marginBottom: "1.5rem" }}>
            <StatCard value={totalMembers} label="Total clientes" icon={Person} accent={colors.sapphire} />
            <StatCard value={activeMembers} label="Membresias activas" icon={AccountBalance} accent="#16a34a" />
            <StatCard value={expiredMembers} label="Membresias vencidas" icon={FilterList} accent="#f59e0b" />
          </div>

          {/* Toolbar */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.75rem",
              marginBottom: "1rem",
              flexWrap: "nowrap",
              overflowX: "auto",
            }}
          >
            {/* Search */}
            <TextField
              size="small"
              placeholder="Buscar por nombre o RUT..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              sx={{
                flex: "0 0 280px",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "0.5rem",
                  backgroundColor: "#fff",
                  fontSize: "0.85rem",
                  height: "38px",
                  "& fieldset": { borderColor: "#e2e8f0" },
                  "&:hover fieldset": { borderColor: "#cbd5e1" },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: "#94a3b8", fontSize: 18 }} />
                  </InputAdornment>
                ),
              }}
            />

            {/* Upload zone */}
            <div
              style={{
                position: "relative",
                border: file ? "1.5px solid " + colors.sapphire : dragOver ? "1.5px dashed " + colors.sapphire : "1.5px dashed #d1d5db",
                borderRadius: "0.5rem",
                padding: "0.375rem 0.875rem",
                flex: "1 1 180px",
                minWidth: 100,
                backgroundColor: dragOver ? colors.sapphire + "08" : file ? colors.sapphire + "05" : "#fff",
                cursor: "pointer",
                transition: "all 0.15s",
                display: "flex",
                alignItems: "center",
                height: 38,
                boxSizing: "border-box",
              }}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={openFileDialog}
            >
              <input ref={fileInputRef} type="file" accept=".xlsx" onChange={(e) => handleFileSelect(e.target.files[0])} style={{ display: "none" }} />
              {!file ? (
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <CloudUpload sx={{ fontSize: 16, color: "#94a3b8" }} />
                  <span style={{ fontSize: "0.8rem", color: "#64748b", fontWeight: 500 }}>Subir .xlsx</span>
                </div>
              ) : (
                <div style={{ display: "flex", alignItems: "center", gap: 6, width: "100%", minWidth: 0 }}>
                  <Description sx={{ fontSize: 16, color: colors.sapphire, flexShrink: 0 }} />
                  <span style={{ fontSize: "0.8rem", fontWeight: 500, color: "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                    {file.name}
                  </span>
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); removeFile(); }}
                    sx={{ padding: "2px", color: "#94a3b8", "&:hover": { color: "#ef4444" } }}
                  >
                    <Close sx={{ fontSize: 14 }} />
                  </IconButton>
                </div>
              )}
            </div>

            {/* Action buttons */}
            <ToolbarBtn
              label={showExpiredOnly ? "Vencidos (ON)" : "Vencidos"}
              onClick={() => setShowExpiredOnly(!showExpiredOnly)}
              active={showExpiredOnly}
            />
            <ToolbarBtn label="Importar" onClick={handleBulkMember} disabled={!file} icon={<Upload sx={{ fontSize: 15 }} />} />
            <ToolbarBtn
              label="Formato"
              href="/plantilla_formato_clientes.xlsx"
              icon={<Download sx={{ fontSize: 15 }} />}
            />
            <ToolbarBtn label="Exportar" onClick={handleExportExcel} icon={<Download sx={{ fontSize: 15 }} />} filled />
            <ToolbarBtn label="Nuevo" onClick={() => navigate("/members/add")} icon={<AddCircle sx={{ fontSize: 15 }} />} filled primary />
          </div>

          {/* Table */}
          <div
            style={{
              background: "#fff",
              borderRadius: "0.75rem",
              border: "1px solid #e2e8f0",
              overflow: "hidden",
            }}
          >
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {["Razón Social", "Tags", "Plan", "RUT", "Termino", "Estado", ""].map((h) => (
                      <TableCell
                        key={h}
                        sx={{
                          backgroundColor: "#f8fafc",
                          color: "#475569",
                          fontWeight: 600,
                          fontSize: "0.75rem",
                          textTransform: "uppercase",
                          letterSpacing: "0.04em",
                          borderBottom: "1px solid #e2e8f0",
                          padding: "0.75rem 1rem",
                        }}
                      >
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {legacy.members.map((m, idx) => (
                    <TableRow
                      key={m._id || idx}
                      onClick={() => seeDetails({ member_id: m._id })}
                      sx={{
                        cursor: "pointer",
                        transition: "background 0.1s",
                        "&:hover": { backgroundColor: "#f8fafc" },
                        "&:last-child td": { borderBottom: 0 },
                      }}
                    >
                      {/* Name */}
                      <TableCell sx={{ padding: "0.625rem 1rem", maxWidth: 300 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          {Array.isArray(m.driveFolders?.workers) && m.driveFolders.workers.length > 0 && (
                            <FolderShared sx={{ fontSize: 16, color: colors.sapphire, flexShrink: 0 }} />
                          )}
                          <span
                            style={{
                              fontWeight: 600,
                              fontSize: "0.85rem",
                              color: "#0f172a",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {m.company_information?.razon_social || m.name}
                          </span>
                        </div>
                      </TableCell>

                      {/* Tags */}
                      <TableCell sx={{ padding: "0.625rem 1rem", maxWidth: 120 }}>
                        <span style={{ fontSize: "0.8rem", color: "#64748b" }}>
                          {m.tags?.join(", ") || ""}
                        </span>
                      </TableCell>

                      {/* Plan */}
                      <TableCell sx={{ padding: "0.625rem 1rem", maxWidth: 180 }}>
                        {m.subscription ? (
                          <Chip
                            label={m.subscription.membership?.title || m.subscription.type || "—"}
                            size="small"
                            sx={{
                              backgroundColor: "#eff6ff",
                              color: "#1d4ed8",
                              fontWeight: 500,
                              fontSize: "0.75rem",
                              height: 24,
                              maxWidth: 170,
                              "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis" },
                            }}
                          />
                        ) : (
                          <span style={{ fontSize: "0.8rem", color: "#94a3b8" }}>—</span>
                        )}
                      </TableCell>

                      {/* RUT */}
                      <TableCell sx={{ padding: "0.625rem 1rem", maxWidth: 120 }}>
                        <span style={{ fontSize: "0.8rem", color: "#475569", fontFamily: "monospace" }}>
                          {m.id_number}
                        </span>
                      </TableCell>

                      {/* End Date */}
                      <TableCell sx={{ padding: "0.625rem 1rem", maxWidth: 100 }}>
                        <span style={{ fontSize: "0.8rem", color: "#64748b" }}>
                          {formatDateLocal(m.subscription?.endDate)}
                        </span>
                      </TableCell>

                      {/* Status */}
                      <TableCell sx={{ padding: "0.625rem 1rem", maxWidth: 90 }}>
                        {m.enabled ? (
                          <Chip
                            label={getStatusText(m.subscription?.endDate)}
                            size="small"
                            sx={{
                              fontWeight: 600,
                              fontSize: "0.7rem",
                              height: 22,
                              minWidth: 60,
                              backgroundColor: new Date(m.subscription?.endDate) > new Date() ? "#dcfce7" : "#fef3c7",
                              color: new Date(m.subscription?.endDate) > new Date() ? "#15803d" : "#a16207",
                            }}
                          />
                        ) : (
                          <Chip label="OFF" size="small" sx={{ fontWeight: 600, fontSize: "0.7rem", height: 22, backgroundColor: "#f1f5f9", color: "#94a3b8" }} />
                        )}
                      </TableCell>

                      {/* Action */}
                      <TableCell sx={{ padding: "0.625rem 1rem", width: 100 }}>
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); seeDetails({ member_id: m._id }); }}
                          sx={{
                            color: "#94a3b8",
                            "&:hover": { color: colors.sapphire, backgroundColor: colors.sapphire + "10" },
                          }}
                        >
                          <VisibilityIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                        {auth.isAdmin && (
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); setDeleteDialog({ open: true, member: m }); }}
                            sx={{
                              color: "#94a3b8",
                              "&:hover": { color: "#ef4444", backgroundColor: "#fef2f2" },
                            }}
                          >
                            <DeleteOutline sx={{ fontSize: 18 }} />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {legacy.members?.length === 0 && !loading && (
              <div style={{ padding: "3rem", textAlign: "center" }}>
                <Person sx={{ fontSize: 48, color: "#d1d5db", marginBottom: "0.75rem" }} />
                <p style={{ color: "#64748b", fontWeight: 600, marginBottom: "0.25rem" }}>No hay clientes</p>
                <p style={{ color: "#94a3b8", fontSize: "0.85rem", marginBottom: "1.5rem" }}>Agrega tu primer cliente o importa una lista</p>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddCircle />}
                  onClick={() => navigate("/members/add")}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: "0.5rem",
                    backgroundColor: colors.sapphire,
                    "&:hover": { backgroundColor: colors.oxfordBlue },
                  }}
                >
                  Agregar cliente
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Delete confirmation dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, member: null })}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: "1.1rem" }}>Eliminar cliente</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "0.9rem" }}>
            ¿Estás seguro de que deseas eliminar a <strong>{deleteDialog.member?.name}</strong>? Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ padding: "0.75rem 1.5rem" }}>
          <Button onClick={() => setDeleteDialog({ open: false, member: null })} sx={{ textTransform: "none", color: "#64748b" }}>
            Cancelar
          </Button>
          <Button
            onClick={() => {
              handleDeleteMember(deleteDialog.member._id);
              setDeleteDialog({ open: false, member: null });
            }}
            variant="contained"
            sx={{ textTransform: "none", backgroundColor: "#ef4444", "&:hover": { backgroundColor: "#dc2626" } }}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

// Toolbar button helper
const ToolbarBtn = ({ label, onClick, icon, disabled, active, filled, primary, href }) => {
  const base = {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "0.4rem 0.75rem",
    fontSize: "0.78rem",
    fontWeight: 600,
    borderRadius: "0.5rem",
    border: "1px solid",
    cursor: disabled ? "not-allowed" : "pointer",
    transition: "all 0.15s",
    whiteSpace: "nowrap",
    textDecoration: "none",
    flexShrink: 0,
  };

  const style = primary
    ? { ...base, background: colors.sapphire, color: "#fff", borderColor: colors.sapphire, opacity: disabled ? 0.5 : 1 }
    : filled
    ? { ...base, background: colors.oxfordBlue + "0a", color: "#334155", borderColor: "#e2e8f0", opacity: disabled ? 0.5 : 1 }
    : active
    ? { ...base, background: colors.sapphire, color: "#fff", borderColor: colors.sapphire }
    : { ...base, background: "#fff", color: "#475569", borderColor: "#e2e8f0", opacity: disabled ? 0.4 : 1 };

  if (href) {
    return (
      <a href={href} download style={style}>
        {icon} {label}
      </a>
    );
  }

  return (
    <button onClick={onClick} disabled={disabled} style={style}>
      {icon} {label}
    </button>
  );
};
